/**
 * Slide Numberer
 * 
 * Utility class for adding and managing slide numbers in carousel components.
 * Handles both slide number overlays and pagination dot numbering.
 */
export class SlideNumberer {
    
    /**
     * Adds number elements to carousel slides
     * @param {HTMLElement[]} slides - Array of slide elements
     * @param {string} numberElementClass - CSS class for number elements (default: 'carousel__slide__num')
     */
    static addSlideNumbers(slides, numberElementClass = 'carousel__slide__num') {
        if (!slides || slides.length === 0) {
            return;
        }
        
        slides.forEach((slide, index) => {
            // Check if slide already has a number element
            const existingNumber = slide.querySelector(`.${numberElementClass}`);
            if (existingNumber) {
                // Update existing number
                existingNumber.textContent = (index + 1).toString();
            } else {
                // Create new number element
                const numberElement = document.createElement('div');
                numberElement.className = numberElementClass;
                numberElement.textContent = (index + 1).toString();
                numberElement.setAttribute('aria-hidden', 'true'); // Decorative, not essential for screen readers
                
                // Insert as first child to ensure it appears on top
                slide.insertBefore(numberElement, slide.firstChild);
            }
        });
        
        console.log('[SlideNumberer] Added numbers to', slides.length, 'slides');
    }
    
    /**
     * Updates existing slide numbers after slides have been reordered
     * @param {HTMLElement[]} slides - Array of slide elements in their current order
     * @param {string} numberElementClass - CSS class for number elements
     */
    static updateSlideNumbers(slides, numberElementClass = 'carousel__slide__num') {
        if (!slides || slides.length === 0) {
            return;
        }
        
        slides.forEach((slide, index) => {
            const numberElement = slide.querySelector(`.${numberElementClass}`);
            if (numberElement) {
                numberElement.textContent = (index + 1).toString();
            }
        });
        
        console.log('[SlideNumberer] Updated numbers for', slides.length, 'slides');
    }
    
    /**
     * Removes all slide number elements from slides
     * @param {HTMLElement[]} slides - Array of slide elements
     * @param {string} numberElementClass - CSS class for number elements
     */
    static removeSlideNumbers(slides, numberElementClass = 'carousel__slide__num') {
        if (!slides || slides.length === 0) {
            return;
        }
        
        slides.forEach(slide => {
            const numberElement = slide.querySelector(`.${numberElementClass}`);
            if (numberElement) {
                numberElement.remove();
            }
        });
        
        console.log('[SlideNumberer] Removed numbers from', slides.length, 'slides');
    }
    
    /**
     * Adds slide numbers to pagination dots
     * @param {HTMLElement[]} dotElements - Array of dot button elements
     */
    static addPaginationNumbers(dotElements) {
        if (!dotElements || dotElements.length === 0) {
            return;
        }
        
        dotElements.forEach((dot, index) => {
            // Set slide number as data attribute for CSS access
            dot.setAttribute('data-slide-number', (index + 1).toString());
            
            // Also set accessible label
            const slideNumber = index + 1;
            dot.setAttribute('aria-label', `Go to slide ${slideNumber}`);
            
            // Set text content (will be hidden by CSS unless numbered pagination is active)
            dot.textContent = slideNumber.toString();
        });
        
        console.log('[SlideNumberer] Added numbers to', dotElements.length, 'pagination dots');
    }
    
    /**
     * Updates pagination dot numbers after reordering
     * @param {HTMLElement[]} dotElements - Array of dot button elements in current order
     */
    static updatePaginationNumbers(dotElements) {
        SlideNumberer.addPaginationNumbers(dotElements);
    }
    
    /**
     * Removes numbering from pagination dots
     * @param {HTMLElement[]} dotElements - Array of dot button elements
     */
    static removePaginationNumbers(dotElements) {
        if (!dotElements || dotElements.length === 0) {
            return;
        }
        
        dotElements.forEach(dot => {
            dot.removeAttribute('data-slide-number');
            dot.removeAttribute('aria-label');
            dot.textContent = '';
        });
        
        console.log('[SlideNumberer] Removed numbers from', dotElements.length, 'pagination dots');
    }
    
    /**
     * Convenience method to add numbers to both slides and pagination in one call
     * @param {HTMLElement[]} slides - Array of slide elements
     * @param {HTMLElement[]} dotElements - Array of pagination dot elements
     * @param {string} slideNumberClass - CSS class for slide number elements
     */
    static addNumbersToCarousel(slides, dotElements, slideNumberClass = 'carousel__slide__num') {
        SlideNumberer.addSlideNumbers(slides, slideNumberClass);
        SlideNumberer.addPaginationNumbers(dotElements);
    }
    
    /**
     * Convenience method to update numbers for both slides and pagination
     * @param {HTMLElement[]} slides - Array of slide elements
     * @param {HTMLElement[]} dotElements - Array of pagination dot elements
     * @param {string} slideNumberClass - CSS class for slide number elements
     */
    static updateCarouselNumbers(slides, dotElements, slideNumberClass = 'carousel__slide__num') {
        SlideNumberer.updateSlideNumbers(slides, slideNumberClass);
        SlideNumberer.updatePaginationNumbers(dotElements);
    }
}